import { useCallback, useEffect, useRef, useState } from 'react'
import { HubConnection } from '@microsoft/signalr'
import { saveAs } from 'file-saver'
import { cisgenicTaskApi } from '@/api/config-setup/cisgenicTaskApi'
import { useSignalR } from '@/signalr/context'
import { ActionSignalR } from '@/types/signalr'
import {
  ActiveTaskResponse,
  CisgenicTaskProgress,
  CisgenicTaskStatus,
  CisgenicTaskUpdateEvent,
} from '@/types/cisgenicTask'

// Step name mapping for UI display
const STEP_DISPLAY_NAMES: Record<string, string> = {
  'init-data-decoders': 'Initializing Data',
  'hydraulic': 'Hydraulic',
  'et-reference-and-plant': 'Plant & ET Reference',
  'soil': 'Soils',
  'emitter': 'Emitter',
  'area': 'Area & Zone',
  'sensor': 'Sensor',
  'et-zone': 'ET Zone',
  'station': 'Station',
  'finalize': 'Finalizing',
}

// Polling interval in milliseconds (5 seconds - reduced frequency since SignalR is primary)
const POLLING_INTERVAL = 5000

// Fallback polling timeout - if no SignalR update after this time, start polling
const SIGNALR_TIMEOUT = 10000

// SignalR group name pattern for task updates
const getTaskGroupName = (interfaceId: number) => `interface:${interfaceId}:tasks`

export interface UseCommonDataUploadV2Options {
  interfaceId: number
  onSuccess?: () => void
  onError?: (error: string) => void
  onCancel?: () => void
}

export interface UseCommonDataUploadV2Return {
  // Actions
  upload: (file: File) => Promise<void>
  checkActiveTask: () => Promise<ActiveTaskResponse | null>
  cancelTask: () => Promise<boolean>

  // State
  isUploading: boolean
  isCheckingActive: boolean
  isCancelling: boolean
  taskId: string | null
  progress: CisgenicTaskProgress | null
  error: string | null

  // Computed
  currentStepName: string | null
  overallProgress: number
  isCompleted: boolean
  isFailed: boolean
  isCancelled: boolean
}

export const useCommonDataUploadV2 = (
  options: UseCommonDataUploadV2Options
): UseCommonDataUploadV2Return => {
  const { interfaceId, onSuccess, onError, onCancel } = options
  const { getHubConnect } = useSignalR()

  // State
  const [isUploading, setIsUploading] = useState(false)
  const [isCheckingActive, setIsCheckingActive] = useState(false)
  const [isCancelling, setIsCancelling] = useState(false)
  const [taskId, setTaskId] = useState<string | null>(null)
  const [progress, setProgress] = useState<CisgenicTaskProgress | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Refs for cleanup
  const connectionRef = useRef<HubConnection | null>(null)
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const signalRTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const isSignalRActiveRef = useRef(false)
  const joinedGroupRef = useRef<string | null>(null)
  const lastStepRef = useRef<string | null>(null)
  const lastSignalRUpdateRef = useRef<number>(0)
  const maxProgressRef = useRef<number>(0)

  /**
   * Clear polling interval
   */
  const clearPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current)
      pollingIntervalRef.current = null
    }
  }, [])

  /**
   * Clear SignalR timeout
   */
  const clearSignalRTimeout = useCallback(() => {
    if (signalRTimeoutRef.current) {
      clearTimeout(signalRTimeoutRef.current)
      signalRTimeoutRef.current = null
    }
  }, [])

  /**
   * Start HTTP polling as fallback
   * Only used when SignalR is not receiving updates
   */
  const startPolling = useCallback(
    (currentTaskId: string) => {
      clearPolling()

      pollingIntervalRef.current = setInterval(async () => {
        try {
          const { data } = await cisgenicTaskApi.getTaskProgress(currentTaskId)

          // Ensure progress only increases
          const effectiveProgress = Math.max(data.progress, maxProgressRef.current)
          maxProgressRef.current = effectiveProgress

          setProgress({
            ...data,
            progress: effectiveProgress,
          })

          // Check if task is completed or failed
          if (
            data.status === CisgenicTaskStatus.Completed ||
            data.status === CisgenicTaskStatus.Failed
          ) {
            clearPolling()
            clearSignalRTimeout()
            setIsUploading(false)

            if (data.status === CisgenicTaskStatus.Completed) {
              onSuccess?.()
            } else if (data.status === CisgenicTaskStatus.Failed) {
              const errorMsg = data.errorMessage || 'Upload failed'
              setError(errorMsg)
              onError?.(errorMsg)
            }
          }
        } catch (err) {
          console.error('[Polling] Error fetching progress:', err)
        }
      }, POLLING_INTERVAL)
    },
    [clearPolling, clearSignalRTimeout, onSuccess, onError]
  )

  /**
   * Start SignalR timeout watchdog
   * If no SignalR update within timeout period, start polling as fallback
   */
  const startSignalRTimeout = useCallback(
    (currentTaskId: string) => {
      clearSignalRTimeout()
      signalRTimeoutRef.current = setTimeout(() => {
        const timeSinceLastUpdate = Date.now() - lastSignalRUpdateRef.current
        if (timeSinceLastUpdate >= SIGNALR_TIMEOUT && !pollingIntervalRef.current) {
          startPolling(currentTaskId)
        }
      }, SIGNALR_TIMEOUT)
    },
    [clearSignalRTimeout, startPolling]
  )

  /**
   * Setup SignalR listener for task updates
   */
  const setupSignalRListener = useCallback(
    async (currentTaskId: string) => {
      try {
        const connection = await getHubConnect()
        connectionRef.current = connection

        // Join the interface tasks group to receive updates
        const groupName = getTaskGroupName(interfaceId)
        await connection.invoke('JoinGroup', groupName)
        joinedGroupRef.current = groupName

        const taskUpdateHandler = async (event: CisgenicTaskUpdateEvent) => {
          // Debug: Log SignalR event for production debugging
          console.log('[SignalR Common Data] Event received:', {
            taskId: event.taskId,
            status: event.status,
            progress: event.progress,
            currentStep: event.currentStep,
            stepProgress: event.stepProgress,
            message: event.message,
            errorMessage: event.errorMessage,
          })

          // Filter by taskId
          if (event.taskId !== currentTaskId) {
            return
          }

          // Mark SignalR as active and record update time
          isSignalRActiveRef.current = true
          lastSignalRUpdateRef.current = Date.now()

          // Reset timeout watchdog since we received an update
          startSignalRTimeout(currentTaskId)

          // Keep last step if current step is null (prevents UI glitch)
          const effectiveStep = event.currentStep ?? lastStepRef.current
          if (event.currentStep) {
            lastStepRef.current = event.currentStep
          }

          // Use SignalR progress as primary source with Math.max protection
          const effectiveProgress = Math.max(event.progress, maxProgressRef.current)
          maxProgressRef.current = effectiveProgress

          // Update progress from SignalR (primary source)
          setProgress(prev => {
            const baseData = prev || {
              taskId: event.taskId,
              status: event.status,
              progress: effectiveProgress,
              currentStep: effectiveStep,
              steps: [],
              estimatedTimeRemaining: null,
              createdAt: new Date().toISOString(),
              startedAt: null,
              completedAt: null,
              errorMessage: null,
            }

            return {
              ...baseData,
              status: event.status,
              progress: effectiveProgress,
              currentStep: effectiveStep,
              steps: event.steps || baseData.steps,
              estimatedTimeRemaining: event.estimatedTimeRemaining,
              errorMessage: event.errorMessage,
            }
          })

          // Check if task is completed or failed
          if (
            event.status === CisgenicTaskStatus.Completed ||
            event.status === CisgenicTaskStatus.Failed
          ) {
            // Fetch final data from API for complete steps information
            try {
              const { data } = await cisgenicTaskApi.getTaskProgress(currentTaskId)
              setProgress(data)
            } catch {
              // Ignore error, use event data
            }

            clearPolling()
            clearSignalRTimeout()
            setIsUploading(false)

            if (event.status === CisgenicTaskStatus.Completed) {
              onSuccess?.()
            } else if (event.status === CisgenicTaskStatus.Failed) {
              const errorMsg = event.errorMessage || 'Upload failed'
              setError(errorMsg)
              onError?.(errorMsg)
            }
          }
        }

        connection.on(ActionSignalR.CisgenicTaskUpdate, taskUpdateHandler)

        return () => {
          if (connection) {
            connection.off(ActionSignalR.CisgenicTaskUpdate, taskUpdateHandler)
          }
        }
      } catch (err) {
        console.error('SignalR connection error:', err)
        // Fallback to polling if SignalR fails
        startPolling(currentTaskId)
        return undefined
      }
    },
    [getHubConnect, interfaceId, clearPolling, clearSignalRTimeout, onSuccess, onError, startPolling, startSignalRTimeout]
  )

  /**
   * Check if there's an active upload task
   */
  const checkActiveTask = useCallback(async (): Promise<ActiveTaskResponse | null> => {
    setIsCheckingActive(true)
    setError(null)

    try {
      const { data } = await cisgenicTaskApi.getActiveUploadTask(interfaceId)

      if (data.hasActiveTask && data.taskId) {
        setTaskId(data.taskId)

        // Get full progress data
        try {
          const progressResponse = await cisgenicTaskApi.getTaskProgress(data.taskId)
          setProgress(progressResponse.data)

          // If task is still processing, start tracking
          if (
            progressResponse.data.status === CisgenicTaskStatus.Processing ||
            progressResponse.data.status === CisgenicTaskStatus.Pending
          ) {
            setIsUploading(true)
            maxProgressRef.current = progressResponse.data.progress
            lastSignalRUpdateRef.current = Date.now()

            // Setup SignalR as primary source
            await setupSignalRListener(data.taskId)

            // Start timeout watchdog for fallback polling
            startSignalRTimeout(data.taskId)

            return data
          }
        } catch (err) {
          console.error('Failed to get task progress:', err)
        }
      }

      setIsCheckingActive(false)
      return data
    } catch (err: any) {
      const errorMsg = err?.response?.data?.message || 'Failed to check active task'
      setError(errorMsg)
      setIsCheckingActive(false)
      return null
    }
  }, [interfaceId, setupSignalRListener, startSignalRTimeout])

  /**
   * Upload file and start tracking
   */
  const upload = useCallback(
    async (file: File) => {
      setIsUploading(true)
      setError(null)
      setTaskId(null)
      setProgress(null)
      isSignalRActiveRef.current = false
      lastStepRef.current = null
      lastSignalRUpdateRef.current = 0
      maxProgressRef.current = 0

      try {
        const formData = new FormData()
        formData.append('file', file)

        const result = await cisgenicTaskApi.uploadCommonDataV2(interfaceId, formData)

        // Handle validation error - backend returns file with error details
        if (result.type === 'validation-error') {
          const originalName = file.name.replace(/\.[^/.]+$/, '')
          const errorFilename = `${originalName}_Errors.xlsx`
          saveAs(result.blob, errorFilename)

          const errorMsg = 'File contains validation errors. Check the downloaded file for details.'
          setError(errorMsg)
          setIsUploading(false)
          onError?.(errorMsg)
          return
        }

        const { data } = result

        if (!data.success || !data.taskId) {
          throw new Error(data.message || 'Upload failed')
        }

        setTaskId(data.taskId)
        lastSignalRUpdateRef.current = Date.now()

        // Setup SignalR as primary source for progress updates
        await setupSignalRListener(data.taskId)

        // Start timeout watchdog for fallback polling
        startSignalRTimeout(data.taskId)
      } catch (err: any) {
        const errorMsg =
          err?.response?.data?.message || err?.message || 'Upload failed'
        setError(errorMsg)
        setIsUploading(false)
        onError?.(errorMsg)
      }
    },
    [interfaceId, setupSignalRListener, startSignalRTimeout, onError]
  )

  /**
   * Cleanup SignalR connection
   */
  const cleanupSignalR = useCallback(() => {
    if (connectionRef.current) {
      connectionRef.current.off(ActionSignalR.CisgenicTaskUpdate)
      if (joinedGroupRef.current) {
        connectionRef.current.invoke('LeaveGroup', joinedGroupRef.current).catch(() => {
          // Ignore errors during cleanup
        })
        joinedGroupRef.current = null
      }
    }
  }, [])

  /**
   * Poll active task endpoint until hasActiveTask is false
   */
  const pollUntilNoActiveTask = useCallback(async (): Promise<boolean> => {
    const POLL_INTERVAL = 2000
    const MAX_WAIT_TIME = 60000
    const startTime = Date.now()

    while (Date.now() - startTime < MAX_WAIT_TIME) {
      try {
        const { data } = await cisgenicTaskApi.getActiveUploadTask(interfaceId)

        if (!data.hasActiveTask) {
          return true
        }
      } catch {
        // Continue polling on error
      }

      await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL))
    }

    // Timeout reached
    return false
  }, [interfaceId])

  /**
   * Cancel the current task
   * After cancelling, polls active endpoint until no active task found
   */
  const cancelTask = useCallback(async (): Promise<boolean> => {
    if (!taskId) {
      return false
    }

    setIsCancelling(true)

    try {
      const { data } = await cisgenicTaskApi.cancelTask(taskId)

      // API returns { message: "Task cancelled successfully" } on success
      // Check for success message or absence of error
      if (data.message?.toLowerCase().includes('cancelled successfully') || data.success) {
        clearPolling()
        clearSignalRTimeout()
        cleanupSignalR()

        // Poll until no active task
        await pollUntilNoActiveTask()

        setIsUploading(false)
        setIsCancelling(false)
        onCancel?.()
        return true
      }

      setIsCancelling(false)
      return false
    } catch (err: any) {
      const errorMsg =
        err?.response?.data?.message || 'Failed to cancel task'
      setError(errorMsg)
      setIsCancelling(false)
      return false
    }
  }, [taskId, clearPolling, clearSignalRTimeout, cleanupSignalR, pollUntilNoActiveTask, onCancel])

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      clearPolling()
      clearSignalRTimeout()
      cleanupSignalR()
    }
  }, [clearPolling, clearSignalRTimeout, cleanupSignalR])

  // Computed values
  const currentStepName = progress?.currentStep
    ? STEP_DISPLAY_NAMES[progress.currentStep] || progress.currentStep
    : null

  const overallProgress = progress?.progress || 0

  const isCompleted = progress?.status === CisgenicTaskStatus.Completed

  const isFailed = progress?.status === CisgenicTaskStatus.Failed

  const isCancelled = progress?.status === CisgenicTaskStatus.Cancelled

  return {
    // Actions
    upload,
    checkActiveTask,
    cancelTask,

    // State
    isUploading,
    isCheckingActive,
    isCancelling,
    taskId,
    progress,
    error,

    // Computed
    currentStepName,
    overallProgress,
    isCompleted,
    isFailed,
    isCancelled,
  }
}

export default useCommonDataUploadV2
